import { isUserRejectedError } from '@funkit/connect-core'
import {
  type LighterRouteType,
  getLighterBaseSymbol,
  getLighterSecureMainnetAddress,
  trackLighterSecureWithdrawal,
} from '@funkit/connect-core/clients/lighter'
import { useCallback, useRef, useState } from 'react'
import type { Address } from 'viem'

import { useFunkitIdentity } from '../../src/identity/IdentityContext'
import { logger } from '../../src/logging/logger'
import type { LighterWithdrawalFlowConfig } from './lighterWithdrawalConfig'

// Module-scoped: closing the sheet mid-withdrawal unmounts the hook, and the
// fresh one a reopen mounts would know nothing about the in-flight submission.
const inFlightSecureWithdrawals = new Set<string>()

/** What makes two presses the same withdrawal rather than two of them. */
function secureWithdrawalKey({
  accountIndex,
  assetIndex,
  routeType,
  amountTokenUnits,
}: {
  accountIndex: string
  assetIndex: number
  routeType: LighterRouteType
  amountTokenUnits: string
}): string {
  return [accountIndex, assetIndex, routeType, amountTokenUnits].join('|')
}

/**
 * @internal Test-only: clear the process-wide in-flight set so suites that
 * reuse withdrawal details stay isolated. Not part of the public API.
 */
export function __resetInFlightSecureWithdrawals(): void {
  inFlightSecureWithdrawals.clear()
}

export type LighterSecureSubmission =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'submitted'; txHash: string; amountTokenUnits: string }
  // Carries the message, not the error: Lighter's SDK surfaces rate limits and
  // nonce collisions as plain strings the screen classifies for itself.
  | { status: 'failed'; message: string }

export interface UseLighterSecureWithdrawalParams {
  lighter: LighterWithdrawalFlowConfig
  /** The user's L2 main-account index — also the tracking key. */
  accountIndex: string | undefined
  /** Connected wallet: Secure locks the destination to it. */
  recipientAddress: Address
}

/**
 * Submits a Lighter Secure withdrawal: the host's L2 signer bridges the asset
 * to Ethereum 1:1, with no relay and no quote in the path. Fun is not in that
 * path, so the direct-execution row is attribution only and fire-and-forget.
 */
export function useLighterSecureWithdrawal({
  lighter,
  accountIndex,
  recipientAddress,
}: UseLighterSecureWithdrawalParams) {
  const { apiKey } = useFunkitIdentity()
  const isSubmittingRef = useRef(false)
  const [submission, setSubmission] = useState<LighterSecureSubmission>({
    status: 'idle',
  })

  const submit = useCallback(
    async ({
      amountTokenUnits,
      assetIndex,
      routeType,
      sourceSymbol,
      withdrawalUSD,
    }: {
      amountTokenUnits: string
      /** Resolved from the account's asset list; never guessed. */
      assetIndex: number
      routeType: LighterRouteType
      /** May carry a ` (Spot)` suffix — tracking wants the base symbol. */
      sourceSymbol: string
      /** Token amount × spot price, for attribution. '0' when unpriced. */
      withdrawalUSD: string
    }) => {
      if (!accountIndex) {
        // Neither rail can address the user without it; the screen blocks the
        // CTA on the same condition, so this is a belt-and-braces guard.
        logger.warn('lighter:withdrawal:secure:noAccountIndex')
        return
      }
      if (isSubmittingRef.current) {
        logger.warn('lighter:withdrawal:secure:already_submitting')
        return
      }
      const key = secureWithdrawalKey({
        accountIndex,
        assetIndex,
        routeType,
        amountTokenUnits,
      })
      if (inFlightSecureWithdrawals.has(key)) {
        logger.warn('lighter:withdrawal:secure:already_in_flight')
        return
      }
      isSubmittingRef.current = true
      inFlightSecureWithdrawals.add(key)
      setSubmission({ status: 'submitting' })

      let txHash: string
      try {
        // Lighter strategies and withdrawals share one L2 signing account, so
        // an in-flight strategy would bump the nonce out from under this.
        await lighter.onBeforeSign?.()

        logger.info('lighter:withdrawal:secure:start', {
          assetIndex,
          routeType,
          amountTokenUnits,
          destinationAddress: recipientAddress,
        })
        txHash = await lighter.sendLighterSecureWithdrawal({
          amountTokenUnits,
          assetIndex,
          routeType,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        if (isUserRejectedError(error)) {
          // Declining is recoverable — the form stays as it was.
          logger.warn('lighter:withdrawal:secure:user_rejected')
          setSubmission({ status: 'idle' })
          return
        }
        // Recovered: the screen offers the on-chain fallback for a rate limit,
        // and a retry otherwise. Not an alertable ERROR.
        logger.warn('lighter:withdrawal:secure:failed', {
          error: message,
          assetIndex,
          routeType,
        })
        setSubmission({ status: 'failed', message })
        return
      } finally {
        isSubmittingRef.current = false
        inFlightSecureWithdrawals.delete(key)
      }

      logger.info('lighter:withdrawal:secure:submitted', {
        amountTokenUnits,
        assetIndex,
        routeType,
        txHash,
      })

      const mainnetTokenAddress = getLighterSecureMainnetAddress(sourceSymbol)
      if (mainnetTokenAddress) {
        // Fire-and-forget — never block the success screen on tracking.
        void trackLighterSecureWithdrawal({
          apiKey,
          logger,
          // Secure locks the destination to the connected wallet, so the L1
          // recipient address doubles as the userId for tracking purposes.
          userId: recipientAddress,
          txHash,
          recipientAddr: recipientAddress,
          assetIndex,
          mainnetTokenAddress,
          amountTokenUnits,
          withdrawalUSD,
        })
      } else {
        // Attribution only, so a symbol we can't map is a monitoring problem,
        // not a user-facing one.
        logger.warn('lighter:withdrawal:secure:unknownMainnetToken', {
          sourceSymbol: getLighterBaseSymbol(sourceSymbol),
          txHash,
        })
      }

      setSubmission({ status: 'submitted', txHash, amountTokenUnits })
    },
    [apiKey, accountIndex, lighter, recipientAddress],
  )

  const reset = useCallback(() => setSubmission({ status: 'idle' }), [])

  return { submission, submit, reset }
}
